import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import BatteryChargingFullRoundedIcon from "@mui/icons-material/BatteryChargingFullRounded";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import AppButton from "../components/ui/AppButton";
import Panel from "../components/ui/Panel";
import { usePageMetadata } from "../context/PageMetadataContext";
import { getLatestEduRelease } from "../services/BuildsAPI";
import "./SetupDownloadsPage.scss";

const sections = [
  ["download", "Download the app"],
  ["device-setup", "Set up your headsets"],
  ["organization", "Connect your organization"],
  ["first-class", "Before your first class"],
  ["help", "Troubleshooting & help"],
];

const publishedDate = (value) => {
  const date = new Date(value);
  return value && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;
};

const ExternalGuide = ({ href, children }) => (
  <AppButton kind="ghost" component="a" href={href} target="_blank" rel="noopener noreferrer" endIcon={<ArrowOutwardRoundedIcon />}>
    {children}
  </AppButton>
);

const HeadsetPicture = () => (
  <svg viewBox="0 0 96 72" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 31v-5C20 12 76 12 76 26v5" />
    <path d="M23 28h50a9 9 0 0 1 9 9v15a9 9 0 0 1-9 9H60l-7-7H43l-7 7H23a9 9 0 0 1-9-9V37a9 9 0 0 1 9-9Z" />
    <path d="M28 41h40M37 47h22" />
  </svg>
);

const connectionSteps = [
  { title: "Get a join code", description: "Use a code from your organization.", picture: <ConfirmationNumberOutlinedIcon /> },
  { title: "Enter it in the app", description: "Open 10k Science Edu and select Join Code.", picture: <HeadsetPicture /> },
  { title: "Start exploring", description: "Browse your organization’s experiences or the default library.", picture: <AutoStoriesOutlinedIcon /> },
];

const classroomChecks = [
  { title: "Charge & connect", description: "Charge the headsets and connect to Wi-Fi.", icon: <BatteryChargingFullRoundedIcon /> },
  { title: "Check your organization", description: "Make sure its experiences appear in the app.", icon: <SchoolOutlinedIcon /> },
  { title: "Try an experience", description: "Open your lesson and let it finish loading.", icon: <PlayCircleOutlineRoundedIcon /> },
  { title: "Check fit & sound", description: "Adjust the headset, audio, and language.", icon: <TuneRoundedIcon /> },
];

const SetupDownloadsPage = () => {
  const { setTitle, setShowTitleInHeader } = usePageMetadata();
  const { hash } = useLocation();
  const [attempt, setAttempt] = useState(0);
  const [download, setDownload] = useState({ status: "loading" });
  const [copyStatus, setCopyStatus] = useState("");

  useEffect(() => {
    setTitle("VR setup");
    setShowTitleInHeader(true);
  }, [setTitle, setShowTitleInHeader]);

  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [hash]);

  useEffect(() => {
    const controller = new AbortController();
    setDownload({ status: "loading" });
    setCopyStatus("");
    getLatestEduRelease({ signal: controller.signal }).then(
      (release) => {
        if (!controller.signal.aborted) setDownload({ status: "ready", release });
      },
      (error) => {
        if (!controller.signal.aborted) {
          setDownload({ status: error.response?.status === 404 ? "empty" : "error" });
        }
      },
    );
    return () => controller.abort();
  }, [attempt]);

  const release = download.release;
  const date = publishedDate(release?.publishedAt);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(release.downloadUrl);
      setCopyStatus("Download link copied. You can share it with your IT team.");
    } catch {
      setCopyStatus("Could not copy the link. Right-click Download app to copy its link.");
    }
  };

  return (
    <main className="setupPage">
      <div className="setupPage__layout">
        <div className="setupPage__content">
          <Panel as="section" row={false} id="download" className="setupPage__download" aria-labelledby="edu-title">
            <div className="setupPage__downloadCopy">
              <h2 id="edu-title">10k Science Edu</h2>
              <p>Bring science to your Meta Quest headsets.</p>
            </div>
            <div className="setupPage__downloadAction">
            {download.status === "loading" && <p role="status" className="setupPage__status">Checking the latest release…</p>}
            {release && (
              <>
                <AppButton kind="primary" component="a" href={release.downloadUrl} download={release.fileName} startIcon={<DownloadRoundedIcon />}>
                  Download app
                </AppButton>
                <details className="setupPage__downloadDetails">
                  <summary>Download details</summary>
                  <dl className="setupPage__release">
                    <div><dt>Version</dt><dd>{release.version}</dd></div>
                    {date && <div><dt>Published</dt><dd>{date}</dd></div>}
                    <div><dt>File</dt><dd>Android APK{Number.isFinite(release.sizeBytes) && release.sizeBytes > 0 && <> · <span>{(release.sizeBytes / 1000000).toLocaleString(undefined, { maximumFractionDigits: 1 })} MB</span></>}</dd></div>
                  </dl>
                  <AppButton kind="ghost" onClick={copyLink} startIcon={<LinkRoundedIcon />}>Copy download link</AppButton>
                  {copyStatus && <p className="setupPage__copyStatus" role="status">{copyStatus}</p>}
                </details>
              </>
            )}
            {["error", "empty"].includes(download.status) && (
              <div className="setupPage__status" role="alert">
                <p>{download.status === "empty" ? "An Edu download is not available yet. You can still follow the setup guidance below." : "We couldn’t load the latest download. Please try again."}</p>
                <AppButton kind="secondary" onClick={() => setAttempt((value) => value + 1)}>Try again</AppButton>
              </div>
            )}
            </div>
          </Panel>

          <section id="device-setup" className="setupPage__section" aria-labelledby="device-setup-title">
            <p className="setupPage__eyebrow">01 / INSTALL</p>
            <h2 id="device-setup-title">Set up your headsets</h2>
            <p>Choose how your school installs apps.</p>
            <p className="setupPage__note">Already have the app? <a href="#organization">Connect your organization</a>.</p>
            <div className="setupPage__options app-row-list">
              <details className="app-row app-row--hoverable">
                <summary className="app-row--interactive">Horizon Managed Services (HMS)<span>Your school may already have the app</span></summary>
                <div className="setupPage__detail">
                  <ol>
                    <li>Look for 10k Science Edu in the headset’s app library.</li>
                    <li>Open it and connect your organization below.</li>
                    <li>App missing? Ask your headset administrator to add it.</li>
                  </ol>
                </div>
              </details>
              <details className="app-row app-row--hoverable">
                <summary className="app-row--interactive">ManageXR or ArborXR<span>Install through your management console</span></summary>
                <div className="setupPage__detail">
                  <ol>
                    <li>Download the app above and upload the APK to your console. If we’ve sent you a platform sharing link, use that instead.</li>
                    <li>Assign the app to your headsets and deploy it over Wi-Fi.</li>
                    <li>When installation finishes, open 10k Science Edu.</li>
                  </ol>
                  <div className="setupPage__actions">
                    <ExternalGuide href="https://help.managexr.com/en/articles/5281561-upload-a-managed-app">ManageXR upload guide</ExternalGuide>
                    <ExternalGuide href="https://help.arborxr.com/en/articles/6333163-upload-apps-to-arborxr">ArborXR upload guide</ExternalGuide>
                  </div>
                </div>
              </details>
              <details className="app-row app-row--hoverable">
                <summary className="app-row--interactive">USB / sideloading<span>Install from a computer</span></summary>
                <div className="setupPage__detail">
                  <ol>
                    <li>Download the app above to your computer.</li>
                    <li>Follow Meta’s device setup guide, then connect the headset with a USB data cable.</li>
                    <li>Approve USB debugging on the headset if prompted.</li>
                    <li>Install the APK with your school’s installation tool, then open 10k Science Edu.</li>
                  </ol>
                  <p>Your IT team can also install the APK using Android Debug Bridge (ADB).</p>
                  <div className="setupPage__actions">
                    <ExternalGuide href="https://developers.meta.com/horizon/documentation/native/android/mobile-device-setup/">Meta device setup</ExternalGuide>
                    <ExternalGuide href="https://developers.meta.com/horizon/documentation/native/android/ts-adb/">Meta USB / ADB guide</ExternalGuide>
                  </div>
                </div>
              </details>
            </div>
          </section>

          <section id="organization" className="setupPage__section" aria-labelledby="organization-title">
            <p className="setupPage__eyebrow">02 / CONNECT</p>
            <h2 id="organization-title">Connect your organization</h2>
            <p>One-time setup on each headset.</p>
            <ol className="setupPage__steps" aria-label="Connect in three steps">
              {connectionSteps.map(({ title, description, picture }, index) => (
                <li key={title}>
                  <span className="setupPage__stepNumber" aria-hidden="true">{index + 1}</span>
                  <div className="setupPage__picture" aria-hidden="true">{picture}</div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </li>
              ))}
            </ol>
            <p className="setupPage__note">Your headset remembers the organization. You’ll only need to reconnect if the app’s saved data is cleared.</p>
          </section>

          <section id="first-class" className="setupPage__section" aria-labelledby="first-class-title">
            <p className="setupPage__eyebrow">03 / GET READY</p>
            <h2 id="first-class-title">Before your first class</h2>
            <ul className="setupPage__checklist">
              {classroomChecks.map(({ title, description, icon }) => (
                <li key={title}>
                  <span className="setupPage__checkIcon" aria-hidden="true">{icon}</span>
                  <div><h3>{title}</h3><p>{description}</p></div>
                </li>
              ))}
            </ul>
            <AppButton kind="secondary" component={Link} to="/">Browse classroom experiences</AppButton>
          </section>

          <section id="help" className="setupPage__section" aria-labelledby="help-title">
            <p className="setupPage__eyebrow">NEED A HAND?</p>
            <h2 id="help-title">Troubleshooting &amp; help</h2>
            <div className="setupPage__options app-row-list">
              <details className="app-row app-row--hoverable">
                <summary className="app-row--interactive">App missing?</summary>
                <div className="setupPage__detail">
                  <ul>
                    <li>Managed headset: check that the app is assigned and installation has finished.</li>
                    <li>USB installation: check that your installer reported success.</li>
                  </ul>
                  <p>Still missing? Ask your headset administrator to check.</p>
                </div>
              </details>
              <details className="app-row app-row--hoverable">
                <summary className="app-row--interactive">Join code not working?</summary>
                <div className="setupPage__detail">
                  <ol>
                    <li>Check Wi-Fi and enter the code again.</li>
                    <li>Ask the person who shared it to check that it’s still valid and belongs to your organization.</li>
                  </ol>
                </div>
              </details>
              <details className="app-row app-row--hoverable">
                <summary className="app-row--interactive">Updating the app?</summary>
                <div className="setupPage__detail">
                  <ul>
                    <li>Managed headset: ask your administrator to deploy the latest version.</li>
                    <li>USB installation: install the latest APK as an update.</li>
                  </ul>
                  <p>Keep the app’s saved data so it remembers your organization.</p>
                </div>
              </details>
            </div>
            <p className="setupPage__note">Still stuck? Tell us what happened and which headset you’re using.</p>
            <AppButton kind="secondary" component={Link} to="/contact">Contact 10k Science</AppButton>
          </section>
        </div>
        <aside className="setupPage__aside">
          <nav aria-label="On this page">
            <p className="setupPage__eyebrow">ON THIS PAGE</p>
            {sections.map(([id, label]) => <AppButton key={id} kind="ghost" component="a" href={`#${id}`}>{label}</AppButton>)}
          </nav>
        </aside>
      </div>
    </main>
  );
};

export default SetupDownloadsPage;
